import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import debounce from 'debounce';
import { filter } from 'ramda';

import ImageCheckbox from '../imageCheckbox/ImageCheckbox';
import Loader from '../loader/Loader';
import Tag from '../tag/Tag';
import {
    MultiSelectItem,
    type DropdownOptionType
} from '../multiSelectDropdown/MultiSelectDropdown';

import { OPTIONS_LENGTH_LIMIT } from '../../js/_constants';
import { useTranslation } from 'react-i18next';
import { isNotEmpty, propNotEq } from '../../js/fpUtils';

import './MultiSelectInput.scss';

export type { DropdownOptionType };

export type MultiSelectInputHintVisibility = 'always' | 'focused';

export type MultiSelectInputProps = {
    autoFocus?: boolean;
    className?: string;
    defaultValue?: DropdownOptionType[];
    disabled?: boolean;
    emptyOptionsText?: string;
    hint?: React.ReactNode;
    hintVisibility?: MultiSelectInputHintVisibility;
    inputValue?: string;
    isLoading?: boolean;
    isSingleSelect?: boolean;
    label: string;
    maxOptionsLength?: number;
    name?: string;
    onChange: (param: DropdownOptionType[]) => void;
    onQueryChange?: (param) => void;
    options: DropdownOptionType[];
    searchPlaceholder?: string;
    withSelectAllButton?: boolean;
};

/**
 * Inline variant of MultiSelectDropdown: selected options render as removable
 * tags directly next to a search input (always visible, no click-to-expand
 * step), so typing/removing reacts immediately instead of needing to open the
 * field first.
 */
const MultiSelectInput = ({
    autoFocus = false,
    className,
    defaultValue,
    disabled = false,
    emptyOptionsText,
    hint,
    hintVisibility = 'always',
    inputValue,
    isLoading = false,
    isSingleSelect,
    label,
    maxOptionsLength = OPTIONS_LENGTH_LIMIT,
    name,
    onChange,
    onQueryChange,
    options,
    searchPlaceholder,
    withSelectAllButton = false
}: MultiSelectInputProps) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);
    const [filterQuery, setFilterQuery] = useState('');
    const [filteredOptions, setFilteredOptions] = useState<
        MultiSelectInputProps['options']
    >([]);
    const [selectedOptions, setSelectedOptions] = useState(defaultValue || []);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleClick = e => {
        if (containerRef.current && containerRef.current.contains(e.target)) {
            // inside click
            return;
        }
        // outside click
        setIsOpen(false);
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClick);

        return () => {
            document.removeEventListener('mousedown', handleClick);
        };
    }, []);

    useEffect(() => {
        setFilteredOptions(
            options.length > OPTIONS_LENGTH_LIMIT
                ? options.slice(0, OPTIONS_LENGTH_LIMIT)
                : options
        );
    }, [options]);

    const isSelected = option =>
        selectedOptions.findIndex(val => val.value === option.value) > -1;

    const handleSingleSelection = (option: DropdownOptionType) => {
        let nextSelected = [...selectedOptions];

        if (isSelected(option)) {
            nextSelected = nextSelected.filter(
                val => val.value !== option.value
            );
        } else if (isSingleSelect) {
            nextSelected = [option];
            setIsOpen(false);
        } else {
            nextSelected.push(option);
        }

        setSelectedOptions(nextSelected);
        onChange(nextSelected);
    };

    const selectAllOptions = () => {
        setSelectedOptions(options);
        onChange(options);
    };

    const deselectAllOptions = () => {
        setSelectedOptions([]);
        onChange([]);
    };

    const handleFiltering = (event: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * We do not allow changing the query if async request is on-flight
         */
        if (isLoading) return;

        event.persist();

        const query = event.target.value;

        setFilterQuery(query);
        setIsOpen(true);

        /**
         * If onQueryChange prop is defined, we exit this function execution,
         * and let parent component handle filtering. For example, async searching.
         */
        if (onQueryChange) {
            return onQueryChange(event.target.value);
        }

        const titleMatchesQuery = option =>
            new RegExp(query.toLowerCase()).test(option.label.toLowerCase());
        const matches = options.filter(titleMatchesQuery);

        if (matches.length > maxOptionsLength) {
            return setFilteredOptions(options.slice(0, maxOptionsLength));
        }

        if (!query) {
            return setFilteredOptions(options.slice(0, maxOptionsLength));
        }

        return debounce(
            () => setFilteredOptions(options.filter(titleMatchesQuery)) as any,
            200
        )();
    };

    const isAllSelected = () => options.length === selectedOptions.length;

    const menuClasses = classNames({
        'b-multi-select-menu': true,
        'is-visible': isOpen
    });

    const removeOption = (optionValue: string) => {
        const nextSelected = filter(propNotEq(optionValue, 'value'))(
            selectedOptions
        );
        setSelectedOptions(nextSelected);
        onChange(nextSelected);
    };

    const handleUnselecting = (optionValue: string) => (
        event: React.MouseEvent<HTMLButtonElement> | string
    ) => {
        if (typeof event !== 'string') {
            event.stopPropagation();
        }

        removeOption(optionValue);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Backspace' && !filterQuery && selectedOptions.length > 0) {
            removeOption(selectedOptions[selectedOptions.length - 1].value);
        }
    };

    const focusInput = () => {
        if (disabled) return;

        setIsOpen(true);
        inputRef.current?.focus();
    };

    return (
        <div
            className={classNames(
                {
                    'b-multi-select-input': true,
                    'is-focused': isOpen,
                    'is-disabled': disabled
                },
                className
            )}
            ref={containerRef}
            title={name}
            data-testid={name}
        >
            <div className="b-multi-select-input__control" onClick={focusInput}>
                {selectedOptions.map(option => (
                    <Tag
                        name={option.label}
                        key={option.value}
                        className="b-multi-select-input__tag"
                        title={option.label}
                        disabled={disabled}
                        onClick={handleUnselecting(option.value)}
                    />
                ))}
                <input
                    ref={inputRef}
                    className="b-multi-select-input__field"
                    type="text"
                    value={filterQuery || inputValue || ''}
                    placeholder={isNotEmpty(selectedOptions) ? '' : (searchPlaceholder || label)}
                    disabled={disabled}
                    autoFocus={autoFocus && !disabled}
                    onChange={handleFiltering}
                    onKeyDown={handleInputKeyDown}
                    onFocus={focusInput}
                    data-testid={name ? `${name}-input` : undefined}
                />
            </div>
            {isOpen && (
                <div className="b-multi-select-options-container">
                    <div className={menuClasses}>
                        {isLoading && <Loader />}
                        {withSelectAllButton &&
                            options.length < OPTIONS_LENGTH_LIMIT && (
                                <ImageCheckbox
                                    className="select-all-button"
                                    value="false"
                                    key="selected_from_playlist"
                                    label={t('select_all')}
                                    check={isAllSelected()}
                                    onCheck={
                                        !isAllSelected()
                                            ? selectAllOptions
                                            : deselectAllOptions
                                    }
                                />
                            )}
                        {filteredOptions.map(option => (
                            <MultiSelectItem
                                key={option.value}
                                label={option.label}
                                selected={isSelected(option)}
                                isLoading={isLoading}
                                disabled={option.isDisabled}
                                countryGroup={option.countryGroup}
                                onClick={() => handleSingleSelection(option)}
                            />
                        ))}
                        {filteredOptions.length >= maxOptionsLength && (
                            <span className="options-length-warning">
                                Displaying first {maxOptionsLength} options. Use
                                search to narrow down options.
                            </span>
                        )}
                        {filteredOptions.length === 0 && (
                            <span className="empty-options-text">
                                {emptyOptionsText ||
                                    t('multi_select_dropdown_empty_options')}
                            </span>
                        )}
                    </div>
                </div>
            )}
            {hint && (hintVisibility === 'always' || isOpen) && (
                <div className="b-multi-select-input__hint">{hint}</div>
            )}
        </div>
    );
};

export default MultiSelectInput;
